import { Divider } from '@mui/material';
import { SelectionContextProvider } from 'common/contexts/SelectionContext';
import * as React from 'react';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { fetchWatchList, WatchList } from 'services/clients/watchList';
import { useCustomSearchParams } from 'common/hooks/customSearchParams';
import { SHIPMENTS_QUERY_PARAMS } from 'pages/Shipments/params';
import { Routes } from 'constants/pages';
import { useLocation } from 'react-router-dom';
import { ActionBar } from './ActionBar';
import { DetailDrawer } from '../DetailDrawer';
import { Search } from '../Search';
import { Table } from '../Table';
import { Header } from '../Header';
import {
  useColumnVisibility,
  usePresetView,
  useTableColumns,
  useWatchlistFilters,
} from '../utilities';
import { Results } from '../Results';
import { MoreFiltersContext } from './ActionBar/filters';
import { getActiveColumns } from '../Table/columns';
import { OnCellClick } from '../Table/cells/types';
import { UnwatchToggleBanner } from '../UnwatchToggleBanner';
import { PresetView } from '../types';

/**
 * The reusable view that will show a list of containers
 * @returns
 */
export const Containers: React.FC = () => {
  const [moreFiltersOpen, setMoreFiltersOpen] = React.useState(false);
  const { setSearchParams } = useCustomSearchParams(SHIPMENTS_QUERY_PARAMS);

  const presetView = usePresetView();
  const currentPage = useLocation().pathname;

  const [columnVisibilityModel, setColumnVisibilityModel] = useColumnVisibility(presetView);
  const filters = useWatchlistFilters(presetView);

  const shipmentsQuery: UseQueryResult<WatchList> = useQuery({
    queryKey: ['watchList', filters],
    queryFn: ({ signal }) => fetchWatchList({ filters, signal }),
    keepPreviousData: moreFiltersOpen,
    enabled: !moreFiltersOpen,
  });

  const containerCount = shipmentsQuery.data?.count || 0;

  const moreFilterContext = React.useMemo(
    () => ({ open: moreFiltersOpen, setOpen: setMoreFiltersOpen }),
    [moreFiltersOpen, setMoreFiltersOpen]
  );

  const onCellClick: OnCellClick = React.useCallback(
    (container) => {
      setSearchParams({ focusedContainer: container.containerNumber });
    },
    [setSearchParams]
  );
  const { columns, setShowTooltip, showTooltip } = useTableColumns(getActiveColumns, onCellClick);

  return (
    <SelectionContextProvider>
      <MoreFiltersContext.Provider value={moreFilterContext}>
        <div className="flex flex-col h-full flex-shrink-0">
          <div className="p-8 pb-3 sticky top-0 bg-white border-gray-200 border-b z-10">
            <div className="space-y-4">
              <div className="flex space-x-6 items-center">
                <Header containerCount={containerCount} presetView={presetView} />
                <Search />
              </div>
              <div className="space-y-3">
                <Divider />
                <ActionBar
                  columnVisibilityModel={columnVisibilityModel}
                  onColumnVisibilityChange={setColumnVisibilityModel}
                  presetView={presetView}
                />
                <Results found={containerCount} loading={shipmentsQuery.isLoading} />
              </div>
            </div>
          </div>

          {presetView !== PresetView.Active && currentPage !== Routes.ShipmentsDeleted && (
            <div className="flex-shrink-0">
              <UnwatchToggleBanner filters={filters} moreFiltersOpen={moreFiltersOpen} />
            </div>
          )}
          <div className="flex-grow">
            <Table
              shipmentsQuery={shipmentsQuery}
              columnVisibilityModel={columnVisibilityModel}
              pinnedColumns={{ left: ['__check__', 'oceanBol', 'containerNumber', 'state'] }}
              columns={columns}
              setShowTooltip={setShowTooltip}
              showTooltip={showTooltip}
              variant="active"
            />
          </div>
        </div>
        <DetailDrawer shipmentsQuery={shipmentsQuery} />
      </MoreFiltersContext.Provider>
    </SelectionContextProvider>
  );
};
Containers.displayName = 'Shipments.Containers';
